/**
 * The calendar of overpasses — every date imagery exists for over what is on screen, with
 * the run's marks on top of them.
 *
 * The point is a number that used to live in psql: on the eight Ukrainian fields 28
 * observations of 120 passed QA, and nothing on screen said so. Now the summary says it in
 * one line and every day says which of the three it is:
 *
 *   accepted  — solid fill, it fed the decision;
 *   rejected  — hatched, and the reason is on hover and in the accessible name;
 *   unscored  — dashed outline, an overpass the run simply never looked at.
 *
 * Fill / hatch / outline carry the mark, colour only repeats it (визуальный контракт). A day
 * is a button: clicking it shows that date's imagery, which is what the whole layer is for.
 *
 * ФИЛЬТР ЖИВЁТ ЗДЕСЬ, а не в пилюле: он сужает шаг стрелками, и осмыслен ровно тогда, когда
 * даты листают. Его нет в разметке вовсе, пока прогона не было: «принято» — метка, которую
 * оставляет только прогон, и включённый фильтр до прогона обнулил бы список и выключил бы
 * продукт (та же ловушка, из-за которой `acceptedOnly` по умолчанию `false`).
 */

import { useId } from "react";

import { T } from "../strings";
import type { CalendarDay, CalendarModel } from "../lib/calendar";
import { formatDate } from "../lib/format";
import "./SatelliteCalendar.css";

const MARK_WORDS: Record<CalendarDay["mark"], string> = {
  accepted: T.qa.clean,
  rejected: T.qa.rejected,
  unscored: T.qa.unscored,
};

/**
 * Окно СЛОВАМИ, из тех же месяцев, что размечают сетку: «июнь–июль». Один месяц — одно
 * слово, три и больше — крайние через тире. `null` — месяцев нет вовсе, и тогда заголовок
 * обходится без окна: пустое «, :» читалось бы как потерянная строка.
 */
const windowWords = (model: CalendarModel): string | null => {
  const labels = model.months.map((month) => month.label);
  if (labels.length === 0) return null;
  if (labels.length === 1) return labels[0]!;
  return `${labels[0]}–${labels.at(-1)}`;
};

interface Props {
  model: CalendarModel;
  selectedDate: string | null;
  onSelectDate: (date: string) => void;
  /** Шаг стрелками только по чистым снимкам. Без обработчика фильтр не рисуется. */
  acceptedOnly?: boolean;
  onAcceptedOnly?: (value: boolean) => void;
}

export function SatelliteCalendar({
  model,
  selectedDate,
  onSelectDate,
  acceptedOnly = false,
  onAcceptedOnly,
}: Props) {
  const cleanWhatId = useId();
  // Прогон был, если он хоть что-то отметил. Иначе фильтровать нечего и нечем.
  const scored = model.accepted + model.rejected > 0;
  if (model.total === 0) {
    return (
      <div
        className="k-calendar"
        data-testid="satellite-calendar"
        role="group"
        aria-label={T.calendar.title}
      >
        <p className="k-caption">{T.calendar.empty}</p>
      </div>
    );
  }

  return (
    <div
      className="k-calendar"
      data-testid="satellite-calendar"
      role="group"
      aria-label={T.calendar.title}
    >
      {/* ОДНО предложение вместо трёх узлов (UX_SPEC §11.5). Прежде «Чистых 12 из 24»,
          «над видимой областью» и месяц над сеткой стояли порознь, и первый читался как счёт
          по выбранному полю. Область и окно стоят В ЗАГОЛОВКЕ, потому что календарь считает
          пролёты над ЭКРАНОМ: слова «поле» он не произносит ни разу. */}
      <div className="k-calendar__summary">
        <span className="k-calendar__count">
          {T.calendar.heading(windowWords(model), model.accepted, model.total)}
        </span>
        <span className="k-caption">
          {T.calendar.counts(model.accepted, model.rejected, model.unscored)}
        </span>
      </div>

      {onAcceptedOnly && scored && (
        <div className="k-calendar__filter">
          <label className="k-calendar__filterlabel">
            <input
              type="checkbox"
              checked={acceptedOnly}
              onChange={(event) => onAcceptedOnly(event.target.checked)}
            />
            <span className="k-caption">{T.calendar.filterClean}</span>
          </label>
          {/* Точное определение гейта — за «i». В подписи ему места нет, а без него
              «чистый» читается как «безоблачный», чем этот фильтр не является. */}
          <span className="k-calendar__why">
            {/* Тот же значок и тот же поповер, что у отметки даты: два вида подсказки на
                одном органе читались бы как два разных механизма. Классы принадлежат пилюле
                (PreviewControl.css) — календарь живёт только внутри неё. */}
            <button
              type="button"
              className="k-datepill__whybtn"
              aria-label={T.calendar.why}
              aria-describedby={cleanWhatId}
            >
              i
            </button>
            <span className="k-datepill__whypop k-caption" id={cleanWhatId}>
              {T.calendar.filterCleanWhat}
            </span>
          </span>
        </div>
      )}

      {model.months.map((month) => (
        <div className="k-calendar__month" key={month.key}>
          <span className="k-calendar__label">{month.label}</span>
          <div className="k-calendar__days">
            {month.days.map((day) => {
              const words = day.inRunWindow
                ? MARK_WORDS[day.mark]
                : `${MARK_WORDS[day.mark]}, ${T.calendar.outsideWindow}`;
              const title = day.reason
                ? `${formatDate(day.date)} — ${words}: ${day.reason}`
                : `${formatDate(day.date)} — ${words}`;
              const classes = [
                "k-calendar__day",
                `k-calendar__day--${day.mark}`,
                day.date === selectedDate ? "k-calendar__day--selected" : "",
                day.available ? "" : "k-calendar__day--unavailable",
                day.inRunWindow ? "" : "k-calendar__day--outside",
              ]
                .filter(Boolean)
                .join(" ");
              return (
                <button
                  type="button"
                  key={day.date}
                  className={classes}
                  data-date={day.date}
                  data-mark={day.mark}
                  title={title}
                  aria-pressed={day.date === selectedDate}
                  aria-label={T.calendar.ariaDay(formatDate(day.date), words, day.reason)}
                  onClick={() => onSelectDate(day.date)}
                >
                  {day.label}
                </button>
              );
            })}
          </div>
        </div>
      ))}

      <div className="k-calendar__legend">
        <span className="k-calendar__legenditem">
          <span className="k-calendar__swatch k-calendar__day--accepted" aria-hidden="true" />
          {T.qa.clean}
        </span>
        <span className="k-calendar__legenditem">
          <span className="k-calendar__swatch k-calendar__day--rejected" aria-hidden="true" />
          {T.qa.rejected}
        </span>
        <span className="k-calendar__legenditem">
          <span className="k-calendar__swatch k-calendar__day--unscored" aria-hidden="true" />
          {T.qa.unscored}
        </span>
      </div>
    </div>
  );
}
